import React, { useEffect, useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import HomeBannerSection from "./pages/HomePage/HomeBannerSection";
import HomeContentSection from "./pages/HomePage/HomeContentSection/HomeContentSection";
import HomeFooterSection from "./pages/HomePage/HomeFooterSection";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import { useAuth } from "./utils";
import DocsPage from "./pages/DocsPage";
import { getUsableStoredAuthToken } from "./authSession";

import { installNativeDragAutoScroll } from "./shared/dragAutoScroll";

const AUTH_THEME_SESSION_KEY = "auth.theme";

const getStoredAuthTheme = () => {
  const storedTheme = sessionStorage.getItem(AUTH_THEME_SESSION_KEY);
  return storedTheme === "night" || storedTheme === "day" ? storedTheme : "day";
};

function App() {
  useEffect(() => installNativeDragAutoScroll(), []);
  const hasStoredToken = Boolean(getUsableStoredAuthToken());
  const [showIntro, setShowIntro] = useState(!hasStoredToken);
  const [showHome, setShowHome] = useState(hasStoredToken);
  const [displayedText, setDisplayedText] = useState("");
  const [fadeOut, setFadeOut] = useState(false);
  const [fadeIn, setFadeIn] = useState(hasStoredToken);
  const [authTheme, setAuthTheme] = useState(getStoredAuthTheme);
  const indexRef = useRef(0);
  const intervalRef = useRef(null);
  const hasPlayedIntroRef = useRef(hasStoredToken);

  const { isAuthenticated } = useAuth();
  const greet = "Hi ~ I'm Sunny Tseng !";

  useEffect(() => {
    if (isAuthenticated) {
      setShowIntro(false);
      setShowHome(true);
      setFadeIn(true);
      hasPlayedIntroRef.current = true;
      return;
    }

    if (hasPlayedIntroRef.current) {
      setShowIntro(false);
      setShowHome(true);
      setFadeIn(true);
      setFadeOut(false);
      return;
    }

    setShowIntro(true);
    setShowHome(false);
    setFadeIn(false);
    setFadeOut(false);
    setDisplayedText("");
    indexRef.current = 0;

    intervalRef.current = setInterval(() => {
      setDisplayedText(greet.slice(0, indexRef.current + 1));
      indexRef.current++;

      if (indexRef.current >= greet.length) {
        clearInterval(intervalRef.current);
        setTimeout(() => {
          setFadeOut(true);
          setTimeout(() => {
            setShowIntro(false);
            setShowHome(true);
            hasPlayedIntroRef.current = true;
            setTimeout(() => setFadeIn(true), 100);
          }, 1600);
        }, 500);
      }
    }, 50);

    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated]);

  useEffect(() => {
    const previous = document.body.getAttribute("data-auth-theme");
    document.body.setAttribute("data-auth-theme", isAuthenticated ? authTheme : "guest");
    return () => {
      if (previous === null) document.body.removeAttribute("data-auth-theme");
      else document.body.setAttribute("data-auth-theme", previous);
    };
  }, [isAuthenticated, authTheme]);

  const toggleAuthTheme = () => {
    setAuthTheme((theme) => {
      const nextTheme = theme === "night" ? "day" : "night";
      sessionStorage.setItem(AUTH_THEME_SESSION_KEY, nextTheme);
      return nextTheme;
    });
  };

  return (
    <Router>
      <ThemeGlobals $authenticated={isAuthenticated} $themeMode={authTheme} />
      {/* Intro Greeting Animation */}
      {!isAuthenticated && showIntro && (
        <IntroContainer $fadeOut={fadeOut}>
          <IntroText>{displayedText}</IntroText>
        </IntroContainer>
      )}

      {/* Authenticated Users */}
      {isAuthenticated && (
        <HomeContainer $authenticated $themeMode={authTheme} data-auth-theme={authTheme}>
          <Routes>
            <Route path="/Login" element={<LoginPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route
              path="*"
              element={
                <>
                  <HomeBannerSection />
                  <HomeContentSection
                    authTheme={authTheme}
                    onToggleAuthTheme={toggleAuthTheme}
                  />
                  <HomeFooterSection />
                </>
              }
            />
          </Routes>
        </HomeContainer>
      )}

      {/* Guest Users */}
      {!isAuthenticated && showHome && (
        <MainContainer $fadeIn={fadeIn}>
          <Routes>
            <Route path="/Login" element={<LoginPage />} />
            <Route path="/docs" element={<Navigate to="/Login" replace />} />
            <Route
              path="*"
              element={
                <HomeContainer $fadeOut={!fadeIn}>
                  <HomeBannerSection />
                  <HomeContentSection />
                  <HomeFooterSection />
                </HomeContainer>
              }
            />
          </Routes>
        </MainContainer>
      )}
    </Router>
  );
}

export default App;

/* ---------- Styled Components ---------- */

const fadeOutAnimation = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    visibility: hidden;
  }
`;

const fadeInAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const IntroContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #171921;
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  transition: opacity 1.6s ease-in-out;

  ${({ $fadeOut }) =>
    $fadeOut &&
    css`
      animation: ${fadeOutAnimation} 1.6s ease-in-out forwards;
    `}
`;

const IntroText = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: white;
  text-align: center;
  font-family: "Arial", sans-serif;
`;

const MainContainer = styled.div`
  opacity: 0;
  transition: opacity 1.6s ease-in-out;

  ${({ $fadeIn }) =>
    $fadeIn &&
    css`
      animation: ${fadeInAnimation} 1.6s ease-in-out forwards;
    `}
`;

const authThemeVariables = css`
  --app-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "transparent" : $themeMode === "day" ? "#f4f6f8" : "#202020"};
  --app-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "inherit" : $themeMode === "day" ? "#202124" : "#eeeeee"};
  --app-muted-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#5f6368" : $themeMode === "day" ? "#5f6368" : "#bebebe"};
  --app-subtle-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#3c4043" : $themeMode === "day" ? "#3c4043" : "#bebebe"};
  --app-border: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#d7d7d7" : $themeMode === "day" ? "#d7d7d7" : "#555555"};
  --app-nav-surface: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#ffffff" : $themeMode === "day" ? "#ffffff" : "#2b2b2b"};
  --app-nav-hover: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#e0e0e0" : $themeMode === "day" ? "#e7eaf0" : "#424242"};
  --app-nav-active-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#b0b0b0" : $themeMode === "day" ? "#202124" : "#4a4a4a"};
  --app-nav-active-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#ffffff" : $themeMode === "day" ? "#ffffff" : "#eeeeee"};
  --app-icon-accent: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#202124" : $themeMode === "day" ? "#202124" : "#eeeeee"};
  --app-theme-toggle-icon: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#334155" : "#f0d892"};
  --app-banner-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#171921" : "#202020"};
  --app-banner-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#ffffff" : $themeMode === "day" ? "#ffffff" : "#eeeeee"};
  --app-surface: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#ffffff" : $themeMode === "day" ? "#ffffff" : "#2b2b2b"};
  --app-surface-hover: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#f9f9f9" : $themeMode === "day" ? "#f9f9f9" : "#424242"};
  --app-surface-soft: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#f4f4f4" : $themeMode === "day" ? "#f4f4f4" : "#333333"};
  --app-surface-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#202124" : $themeMode === "day" ? "#202124" : "#eeeeee"};
  --app-input-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#ffffff" : $themeMode === "day" ? "#ffffff" : "#2b2b2b"};
  --app-input-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#202124" : $themeMode === "day" ? "#202124" : "#eeeeee"};
  --app-question-card-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#ffffff" : $themeMode === "day" ? "#ffffff" : "#2b2b2b"};
  --app-question-detail-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#f7f8fa" : $themeMode === "day" ? "#f7f8fa" : "#333333"};
  --app-warning-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#fff8e5" : $themeMode === "day" ? "#fff8e5" : "#4a3f25"};
  --app-warning-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#4f3a05" : $themeMode === "day" ? "#4f3a05" : "#f2d58a"};
  --app-success-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#f2fbf5" : "#263d2d"};
  --app-success-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#17642c" : "#a9dcb7"};
  --app-success-border: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#a7d8b5" : "#496b52"};
  --app-danger-border: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#efb5b5" : "#6e4444"};
  --app-danger-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#fff4f4" : $themeMode === "day" ? "#fff4f4" : "#4a2d2d"};
  --app-danger-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated ? "#9f1d1d" : $themeMode === "day" ? "#9f1d1d" : "#f0aaa5"};

  --app-modal-overlay: rgba(64, 64, 64, 0.72);
  --app-code-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#111827" : "#2b2b2b"};
  --app-code-header-bg: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#0b1220" : "#333333"};
  --app-code-border: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#263241" : "#555555"};
  --app-code-text: ${({ $authenticated, $themeMode }) =>
    !$authenticated || $themeMode === "day" ? "#e5e7eb" : "#eeeeee"};
`;

const ThemeGlobals = createGlobalStyle`
  :root {
    ${authThemeVariables}
  }

  body[data-auth-theme="night"], body[data-auth-theme="day"] {
    background: var(--app-bg);
    color: var(--app-text);
  }

  body.swal2-shown:not(.swal2-toast-shown):not(.swal2-no-backdrop) .swal2-container.swal2-backdrop-show {
    background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
  }

  body.swal2-toast-shown .swal2-container {
    background: transparent;
  }

  body[data-auth-theme="night"] .swal2-popup {
    background: var(--app-surface) !important;
    color: var(--app-surface-text);
    color-scheme: dark;
  }

  /* SweetAlert writes these masks inline; keep them in sync when the theme changes. */
  body[data-auth-theme="day"] .swal2-success-circular-line-left,
  body[data-auth-theme="day"] .swal2-success-circular-line-right,
  body[data-auth-theme="day"] .swal2-success-fix,
  body[data-auth-theme="night"] .swal2-success-circular-line-left,
  body[data-auth-theme="night"] .swal2-success-circular-line-right,
  body[data-auth-theme="night"] .swal2-success-fix {
    background: var(--app-surface) !important;
  }

  body[data-auth-theme="night"] .swal2-title,
  body[data-auth-theme="night"] .swal2-html-container,
  body[data-auth-theme="night"] .swal2-input-label,
  body[data-auth-theme="night"] .swal2-footer {
    color: var(--app-surface-text);
  }

  body[data-auth-theme="night"] .swal2-styled.swal2-confirm {
    background-color: var(--app-nav-active-bg);
    color: var(--app-nav-active-text);
  }

  body[data-auth-theme="night"] .swal2-styled.swal2-cancel {
    background-color: var(--app-surface-hover);
    color: var(--app-surface-text);
  }

  body[data-auth-theme="night"] .swal2-styled.swal2-deny {
    background-color: var(--app-danger-bg);
    color: var(--app-danger-text);
  }

  body[data-auth-theme="night"] .swal2-close {
    color: var(--app-muted-text);
  }

  body[data-auth-theme="night"] .swal2-close:hover {
    color: var(--app-surface-text);
  }

  body[data-auth-theme="night"] .swal2-timer-progress-bar {
    background: var(--app-muted-text);
  }

  body[data-auth-theme="night"] .swal2-input,
  body[data-auth-theme="night"] .swal2-textarea,
  body[data-auth-theme="night"] .swal2-select,
  body[data-auth-theme="night"] .swal2-select option,
  body[data-auth-theme="night"] .swal2-range,
  body[data-auth-theme="night"] .swal2-radio,
  body[data-auth-theme="night"] .swal2-checkbox {
    background: var(--app-input-bg);
    color: var(--app-input-text);
    border-color: var(--app-border);
  }

  body[data-auth-theme="night"] .swal2-validation-message {
    background: var(--app-danger-bg);
    color: var(--app-danger-text);
  }

  body[data-auth-theme="night"] .swal2-footer {
    border-color: var(--app-border);
  }
`;

const HomeContainer = styled.div`
  ${authThemeVariables}

  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ $authenticated }) =>
    $authenticated ? "var(--app-bg)" : "transparent"};
  color: ${({ $authenticated }) =>
    $authenticated ? "var(--app-text)" : "inherit"};
  opacity: 1;
  transition: opacity 1.6s ease-in-out;

  &[data-auth-theme="night"] .card {
    background-color: var(--app-surface);
    border-color: var(--app-border);
    color: var(--app-surface-text);
  }

  &[data-auth-theme="night"] .card .text-muted {
    color: var(--app-muted-text) !important;
  }
`;
